import { isLineKindVisible, type MapAppearance } from './mapAppearance'
import { pinTypeVisibility } from './mapPinTypes'
import { preparePrintArtwork } from './printArtwork'
import type { PrintProjector } from './printProjection'
import { groundExtentMetres } from './printScale'
import { sheetRect } from './printGeometry'
import type { GeoPt, MapObject, TeamMap } from './types'
import type { PaperFormat, PrintOrientation } from './printPaper'
import { printLineNameAnchor } from './printNameGeometry'
import { roundCoord } from './printGeometry'

export interface PrintOverlay {
  id: string
  label: string
}
export type PrintSkipKind =
  | 'artworkFallback'
  | 'missingPins'
  | 'customGlyphs'
  | 'customBarePins'
  | 'labelBoxes'
  | 'pinOpacity'
  | 'labelOpacity'
  | 'overlay'
  | 'baseMap'
export interface PrintSkip {
  kind: PrintSkipKind
  count: number
  name?: string
}

export interface PrintContentOptions {
  map: TeamMap
  appearance: MapAppearance
  hiddenObjectIds: ReadonlySet<string>
  symbolSvgs: Record<string, string>
  /** The with-background drawings, for telling a colour override apart; defaults to `symbolSvgs`. */
  symbolDesignSvgs?: Record<string, string>
  /** The printed label size in mm; 0 prints no names. Defaults to the pin-names switch. */
  labelMm?: number
  activeOverlays: readonly PrintOverlay[]
  capabilities: readonly string[]
  baseMapSubstituted?: boolean
  propertyBorders?: boolean
  center: GeoPt
  sheet: PrintProjector
  scale: number
  format: PaperFormat
  orientation: PrintOrientation
  customPage?: { w: number; h: number }
  marginMm?: number
}

/** Same anchor test for notices and payloads, excluding the stroke clip buffer. */
export function printAnchorVisible(
  point: GeoPt,
  options: Pick<
    PrintContentOptions,
    | 'sheet'
    | 'center'
    | 'scale'
    | 'format'
    | 'orientation'
    | 'customPage'
    | 'marginMm'
  >,
): boolean {
  const {
    sheet,
    center,
    scale,
    format,
    orientation,
    customPage: page,
    marginMm,
  } = options
  const extent = groundExtentMetres({
    scale,
    format,
    orientation,
    page,
    marginMm,
  })
  const k = sheet.crsMetresPerGroundMetre(center.lat)
  const rect = sheetRect(
    sheet.forward(center),
    { w: extent.w * k, h: extent.h * k },
    0,
  )
  const [x, y] = sheet.forward(point)
  return x >= rect.minX && x <= rect.maxX && y >= rect.minY && y <= rect.maxY
}

/** Invalid or unavailable art falls back through the exact same pipeline. */
export function printPinArtwork(
  object: MapObject,
  options: Pick<PrintContentOptions, 'appearance' | 'symbolSvgs' | 'symbolDesignSvgs'>,
  pinMm: number,
) {
  const { appearance, symbolSvgs } = options
  const designSvgs = options.symbolDesignSvgs ?? symbolSvgs
  if (object.type === 'custom' && !appearance.pinBackground) return null
  for (const key of [...new Set([object.type, 'pin'])]) {
    if (!symbolSvgs[key]) continue
    try {
      return {
        fallback: key !== object.type,
        ...preparePrintArtwork(
          key,
          symbolSvgs[key],
          designSvgs[key] ?? symbolSvgs[key],
          appearance.pinBackground,
          pinMm,
          object.color,
        ),
      }
    } catch {
      /* Try the generic pin before reporting an omitted location. */
    }
  }
  return null
}

/** Visible differences from the deployed print contract, known before Create. */
export function collectPrintSkips(options: PrintContentOptions): PrintSkip[] {
  const { map, appearance: ap, hiddenObjectIds, activeOverlays } = options
  const visible = pinTypeVisibility(ap.hiddenPinTypes)
  const pins = (map.objects ?? []).filter(
    (p) =>
      visible(p.type) &&
      !hiddenObjectIds.has(p.id) &&
      printAnchorVisible(p, options),
  )
  const lines = (map.lines ?? []).filter(
    (l) =>
      !hiddenObjectIds.has(l.id) &&
      isLineKindVisible(ap, l.kind) &&
      l.name?.trim() &&
      l.points.length >= 2 &&
      l.points.some(
        (p) =>
          roundCoord(p.lon) !== roundCoord(l.points[0].lon) ||
          roundCoord(p.lat) !== roundCoord(l.points[0].lat),
      ),
  )
  const skips: PrintSkip[] = []
  const add = (kind: PrintSkipKind, count: number, name?: string) => {
    if (count) skips.push({ kind, count, ...(name ? { name } : {}) })
  }
  let fallback = 0,
    missing = 0,
    custom = 0,
    bare = 0
  for (const pin of pins) {
    if (pin.type === 'custom') {
      if (!ap.pinBackground) {
        bare++
        continue
      }
      custom++
    }
    const art = printPinArtwork(pin, options, 1)
    if (!art) missing++
    else if (art.fallback) fallback++
  }
  add('artworkFallback', fallback)
  add('missingPins', missing)
  add('customGlyphs', custom)
  add('customBarePins', bare)
  // The print panel's label size decides whether names print at all; without
  // one, the pin-names switch does, as the panel's slider starts from it.
  const printsLabels =
    options.labelMm !== undefined ? options.labelMm > 0 : ap.pinNamesVisible
  const labels =
    printsLabels &&
    (pins.some((p) => p.label?.trim()) ||
      lines.some((l) => {
        // No pill: the sheet prints every name without one (printPinLabelStyle).
        const anchor = printLineNameAnchor(
          l,
          false,
          options.scale,
          options.center.lat,
          options.labelMm,
        )
        return anchor !== null && printAnchorVisible(anchor, options)
      }))
  // Keep these notices until the builder emits the corresponding style fields.
  // A newer probe alone does not mean this client has implemented them.
  add('labelBoxes', labels && ap.labelBackground ? 1 : 0)
  add('pinOpacity', pins.length && ap.pinOpacity < 100 ? 1 : 0)
  add('labelOpacity', labels && ap.labelOpacity < 100 ? 1 : 0)
  for (const overlay of activeOverlays) {
    if (overlay.id === 'world-property' && options.propertyBorders) continue
    add('overlay', 1, overlay.label)
  }
  add('baseMap', options.baseMapSubstituted ? 1 : 0)
  return skips
}
